/**
 * MyMemoryPanel — user-facing CRUD over /api/memory/user/<my-id>.
 *
 * Mounts inside personal Settings alongside Feishu and
 * the MCP Tokens panel. PP-CF stores user memory in the
 * scoped_memory D1 table; the user-scope subset is mediated through
 * /api/memory which gates access to scope_id === ctx.user.id.
 *
 * Scope intentionally limited to user-scope. Workspace memory lives
 * in workspace settings (separate page); org memory is admin-only
 * and lives in the AdminDashboard Memory tab.
 *
 * Aligns PP main /dev branch MyMemoryPanel.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { SpinnerGap, Trash, Plus, ArrowsClockwise, FileText, Key } from '@phosphor-icons/react';
import { apiFetch } from '../../api/auth-fetch';

interface MemoryListEntry {
  key: string;
  size: number;
  content_type: string;
  updated_at: number;
}

interface MemoryFile extends MemoryListEntry {
  value: string;
}

interface MyMemoryPanelProps {
  userId: string;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { ...((init.headers as Record<string, string>) || {}) };
  if (init.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const res = await apiFetch(path, { ...init, headers });
  const text = await res.text();
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = JSON.parse(text); if (j?.error?.message) msg = j.error.message; else if (j?.error) msg = j.error; } catch {}
    throw new Error(msg);
  }
  if (!text) return undefined as T;
  try { return JSON.parse(text) as T; } catch { return text as unknown as T; }
}

export default function MyMemoryPanel({ userId }: MyMemoryPanelProps) {
  const [files, setFiles] = useState<MemoryListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFile, setActiveFile] = useState<MemoryFile | null>(null);
  const [draft, setDraft] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api<{ entries: MemoryListEntry[] }>(`/api/memory/user/${encodeURIComponent(userId)}`);
      setFiles(r.entries || []);
    } catch (e: any) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const openFile = async (key: string) => {
    setError('');
    setCreating(false);
    setNewKey('');
    try {
      const r = await api<MemoryFile>(`/api/memory/user/${encodeURIComponent(userId)}/${encodeURIComponent(key)}`);
      setActiveFile(r);
      setDraft(r.value);
    } catch (e: any) {
      setError(e?.message || 'Read failed');
    }
  };

  const startNew = () => {
    setActiveFile(null);
    setCreating(true);
    setNewKey('');
    setDraft('');
  };

  const save = async () => {
    const key = creating ? newKey.trim() : activeFile?.key;
    if (!key) return;
    setSaving(true);
    setError('');
    try {
      await api(`/api/memory/user/${encodeURIComponent(userId)}/${encodeURIComponent(key)}`, {
        method: 'POST',
        body: JSON.stringify({ content: draft }),
      });
      await refresh();
      await openFile(key);
      setCreating(false);
      setNewKey('');
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!activeFile) return;
    if (!window.confirm(`Delete "${activeFile.key}"?`)) return;
    setSaving(true);
    setError('');
    try {
      await api(`/api/memory/user/${encodeURIComponent(userId)}/${encodeURIComponent(activeFile.key)}`, {
        method: 'DELETE',
      });
      setActiveFile(null);
      setDraft('');
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const sectionStyle: React.CSSProperties = {
    border: '1px solid var(--border)', borderRadius: 12,
    background: 'var(--bg-surface)', padding: '20px 24px',
    marginTop: 24,
  };

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h3 style={{ fontSize: 'var(--fs-md)', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Key size={16} weight="light" /> My Memory
        </h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => void refresh()}
            title="Refresh"
            style={{
              padding: '6px 10px', fontSize: 'var(--fs-xs)',
              background: 'transparent', color: 'var(--text-secondary)',
              border: '1px solid var(--border)', borderRadius: 6,
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <ArrowsClockwise size={12} weight="light" /> Refresh
          </button>
          <button
            onClick={startNew}
            style={{
              padding: '6px 12px', fontSize: 'var(--fs-xs)', fontWeight: 600,
              background: 'var(--brand)', color: 'var(--text-on-brand)',
              border: 'none', borderRadius: 6,
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <Plus size={12} weight="bold" /> New
          </button>
        </div>
      </div>
      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', marginTop: 0, marginBottom: 16 }}>
        Notes and preferences in your personal memory scope. The agent reads
        these alongside the current deck's task memory. Use
        <code style={{ marginLeft: 4, marginRight: 4 }}>pp save memory --scope user &lt;key&gt;</code>
        from inside a deck to write here from the sandbox.
      </p>

      {error && (
        <div style={{ padding: '8px 12px', background: 'var(--ds-danger-soft)', border: '1px solid var(--ds-danger-soft)', borderRadius: 8, color: 'var(--ds-danger)', fontSize: 'var(--fs-sm)', marginBottom: 12 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 12, alignItems: 'stretch' }}>
        {/* File list */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, minHeight: 300, maxHeight: 480, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>
              <SpinnerGap size={12} className="animate-spin" /> Loading…
            </div>
          ) : files.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)', padding: 8 }}>
              No memory entries yet. Click "New" to add one.
            </div>
          ) : (
            files.map((f) => (
              <button
                key={f.key}
                onClick={() => void openFile(f.key)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '6px 10px', fontSize: 'var(--fs-xs)',
                  background: activeFile?.key === f.key ? 'var(--bg-accent, var(--ds-accent-soft))' : 'transparent',
                  color: activeFile?.key === f.key ? 'var(--brand, #0033FF)' : 'var(--text-primary)',
                  border: 'none', borderRadius: 4, cursor: 'pointer',
                  fontFamily: 'inherit', marginBottom: 1,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <FileText size={12} weight="light" />
                  {f.key}
                </div>
                <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', marginLeft: 16 }}>
                  {Math.max(1, Math.round(f.size / 1024))} KB · {new Date(f.updated_at).toLocaleDateString()}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Editor */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 300 }}>
          {!activeFile && !creating ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
              Pick an entry on the left, or click "New".
            </div>
          ) : (
            <>
              {creating ? (
                <div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                    New key
                  </div>
                  <input
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    placeholder="e.g. preferences/tone.md"
                    autoFocus
                    style={{ width: '100%', padding: '6px 10px', fontSize: 'var(--fs-sm)', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-primary)', color: 'var(--text-primary)', fontFamily: 'monospace', boxSizing: 'border-box' }}
                  />
                </div>
              ) : activeFile ? (
                <div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                    Key
                  </div>
                  <div style={{ fontSize: 'var(--fs-sm)', fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                    {activeFile.key}
                  </div>
                  <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', marginTop: 4 }}>
                    {activeFile.content_type} · updated {new Date(activeFile.updated_at).toLocaleString()}
                  </div>
                </div>
              ) : null}

              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Markdown / JSON / plain text…"
                style={{
                  flex: 1, minHeight: 220,
                  padding: 10, fontSize: 'var(--fs-sm)',
                  border: '1px solid var(--border)', borderRadius: 6,
                  background: 'var(--bg-primary)', color: 'var(--text-primary)',
                  fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box',
                }}
              />

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => void save()}
                  disabled={saving || (creating && !newKey.trim())}
                  style={{
                    padding: '6px 14px', fontSize: 'var(--fs-sm)', fontWeight: 600,
                    background: 'var(--brand, #0033FF)', color: 'var(--text-on-brand)',
                    border: 'none', borderRadius: 8,
                    cursor: saving || (creating && !newKey.trim()) ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                    opacity: saving || (creating && !newKey.trim()) ? 0.55 : 1,
                  }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                {activeFile && !creating && (
                  <button
                    onClick={() => void remove()}
                    disabled={saving}
                    style={{
                      padding: '6px 14px', fontSize: 'var(--fs-sm)',
                      background: 'transparent', color: 'var(--ds-danger)',
                      border: '1px solid var(--ds-danger-soft)', borderRadius: 8,
                      cursor: 'pointer', fontFamily: 'inherit',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    <Trash size={12} weight="light" /> Delete
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
